import cloneDeep from 'lodash.clonedeep'

import { ApplicantStage } from 'src/graphql/types/applicants'
import { defaultFilters } from 'src/pages/ApplicantsPage/components/FiltersSiderbar/Filters'

const stringToDate = (dateAsString) => {
  const date = new Date(dateAsString)
  return isNaN(date.getTime()) ? null : date
}

export function loadFiltersFromUrl({
  jobListings,
  recruiters,
  tags,
  customFields = [],
}) {
  const searchParams = new URLSearchParams(location.search)
  const filtersCopy = cloneDeep(defaultFilters)

  const jobListingIdFromUrl = searchParams.get('jobListingId')

  for (const filterKey in filtersCopy) {
    const filter = filtersCopy[filterKey]

    if (filter.key === 'jobListings') {
      const positions = jobListings.map((jobListing) => {
        return {
          value: jobListing.jobListingId,
          label: jobListing.title,
          selected:
            jobListingIdFromUrl &&
            jobListingIdFromUrl === jobListing.jobListingId,
        }
      })
      if (jobListingIdFromUrl) {
        filter.selectedValues = [jobListingIdFromUrl]
      }
      filter.options = positions
    }

    if (filter.key === 'recruiters') {
      const employees = recruiters.map((recruiter) => {
        return {
          value: recruiter.employeeId,
          label: `${recruiter.firstName} ${recruiter.lastName}`,
          selected: false,
        }
      })
      filter.options = employees
    }

    if (filter.key === 'tags') {
      const tagOptions = tags.map((tag) => ({
        value: tag.tagId,
        label: tag.label,
        selected: false,
      }))
      filter.options = tagOptions
    }

    const hasDateRange =
      filter.type === 'date-range' &&
      (searchParams.has(`${filter.key}Gte`) ||
        searchParams.has(`${filter.key}Lte`))

    const hasRange =
      filter.type === 'range' &&
      (searchParams.has(`${filter.key}Gte`) ||
        searchParams.has(`${filter.key}Lte`))

    if (searchParams.get(filterKey) || hasDateRange || hasRange) {
      if (searchParams.get('searchText')) {
        if (filter.key === 'searchText') {
          filter.searchText = searchParams.get('searchText')
        }
      }

      if (filter.type === 'multi-select') {
        filter.selectedValues = searchParams.get(filterKey).split(',')
        // Only map over options if they exist (for dynamic filters, options are set earlier)
        // For static filters, options come from defaultFilters
        if (filter.options && filter.options.length > 0) {
          filter.options = filter.options.map((option) => {
            return {
              ...option,
              selected: filter.selectedValues?.includes(option.value),
            }
          })
        }
      }

      if (filter.type === 'step-filter') {
        const stepOptions = searchParams.get(filterKey).split(',')
        const selectedValues = []
        const optionsMap = {}
        const validStepTypes = new Set(filter.options.map((o) => o.value))

        stepOptions.forEach((stepOption) => {
          if (stepOption.startsWith('CUSTOM:')) {
            const [, customStepId, stepStatus] = stepOption.split(':')
            selectedValues.push({
              type: 'CUSTOM',
              customStepId,
              status: stepStatus,
            })
            return
          }

          const [stepType, stepStatus] = stepOption.split('-')

          if (!validStepTypes.has(stepType)) return

          selectedValues.push({ type: stepType, status: stepStatus })

          if (optionsMap[stepType]) {
            optionsMap[stepType].add(stepStatus)
          } else {
            optionsMap[stepType] = new Set()
            optionsMap[stepType].add(stepStatus)
          }
        })

        filter.options = filter.options.map((stepOption) => {
          if (optionsMap[stepOption.value]) {
            stepOption.selected = true
            stepOption.options = stepOption.options.map((option) => {
              return {
                ...option,
                selected: optionsMap[stepOption.value].has(option.value),
              }
            })
          }
          return stepOption
        })
        filter.selectedValues = selectedValues
      }
      if (filter.type === 'applicant-stage-filter') {
        const applicantStageOptions = searchParams.get(filterKey).split(',')
        const selectedValues = []
        const optionsMap = {}

        applicantStageOptions.forEach((applicantStageOption) => {
          const [applicantStage, subOption] = applicantStageOption.split('-')
          if (applicantStage === ApplicantStage.PROSPECT) {
            selectedValues.push({ applicantStage, hiringStage: subOption })
          }
          if (applicantStage === ApplicantStage.DISQUALIFIED) {
            selectedValues.push({
              applicantStage,
              disqualifiedReason: subOption,
            })
          }
          if (applicantStage === ApplicantStage.NOT_INTERESTED) {
            selectedValues.push({
              applicantStage,
              notInterestedReason: subOption,
            })
          }
          if (applicantStage === ApplicantStage.TERMINATED) {
            selectedValues.push({
              applicantStage,
              terminatedReason: subOption,
            })
          }
          if (applicantStage === ApplicantStage.HIRED) {
            selectedValues.push({
              applicantStage,
            })
          }

          if (optionsMap[applicantStage]) {
            optionsMap[applicantStage].add(subOption)
          } else {
            optionsMap[applicantStage] = new Set()
            optionsMap[applicantStage].add(subOption)
          }
        })

        filter.options = filter.options.map((applicantStageOption) => {
          if (optionsMap[applicantStageOption.value]) {
            applicantStageOption.selected = true
            applicantStageOption.options = applicantStageOption.options.map(
              (option) => {
                return {
                  ...option,
                  selected: optionsMap[applicantStageOption.value].has(
                    option.value
                  ),
                }
              }
            )
          }
          return applicantStageOption
        })
        filter.selectedValues = selectedValues
      }

      if (filter.type === 'range') {
        const gteParam = searchParams.get(`${filter.key}Gte`)
        const lteParam = searchParams.get(`${filter.key}Lte`)
        if (gteParam !== null) {
          filter.gte = parseFloat(gteParam)
        }
        if (lteParam !== null) {
          filter.lte = parseFloat(lteParam)
        }
      }

      if (filter.type === 'date-range') {
        const gteParam = searchParams.get(`${filter.key}Gte`)
        const lteParam = searchParams.get(`${filter.key}Lte`)
        if (gteParam) {
          filter.gte = stringToDate(gteParam)
        }
        if (lteParam) {
          filter.lte = stringToDate(lteParam)
        }
      }

      if (filter.type === 'location') {
        const value = searchParams.get(filterKey)
        filter.values = value ? value.split(',') : []
      }
    }
  }

  // Parse custom fields from URL (params starting with cf_)
  const customFieldFilters = []
  Array.from(searchParams.entries()).forEach(([key, value]) => {
    if (key.startsWith('cf_') && value) {
      const customFieldId = key.replace('cf_', '')

      // Look up the field to determine its type
      const fieldMetadata = customFields.find(
        (f) => f.customFieldId === customFieldId
      )

      let filterValue: any = { customFieldId }

      // Check if it's a JSON date range (unambiguous, check first)
      if (value.startsWith('{') && value.includes('gte')) {
        try {
          const dateRange = JSON.parse(value)
          filterValue.dateValue = {
            gte: dateRange.gte ? stringToDate(dateRange.gte) : null,
            lte: dateRange.lte ? stringToDate(dateRange.lte) : null,
          }
        } catch (e) {
          // If JSON parse fails, treat as string
          filterValue.stringValue = value
        }
      } else if (fieldMetadata) {
        // If we have field metadata, use it to determine the type
        // This ensures correct parsing for select fields with values like "true" or "false"
        if (fieldMetadata.fieldType === 'BOOLEAN') {
          // Boolean field - parse true/false
          filterValue.booleanValue = value === 'true'
        } else if (fieldMetadata.fieldType === 'DATE') {
          // Date field - should be JSON, but if not, treat as string for now
          filterValue.stringValue = value
        } else if (fieldMetadata.fieldType === 'MULTI_SELECT') {
          // Multi-select field - try JSON first (handles values with commas)
          // Fall back to comma-split for backward compatibility
          try {
            const parsed = JSON.parse(value)
            if (Array.isArray(parsed)) {
              filterValue.selectValues = parsed
            } else {
              // Not a JSON array, treat as single value
              filterValue.selectValues = [value]
            }
          } catch (e) {
            // Not valid JSON, fall back to comma-split for backward compatibility
            filterValue.selectValues = value.includes(',')
              ? value.split(',')
              : [value]
          }
        } else if (fieldMetadata.fieldType === 'SINGLE_SELECT') {
          // Single select field - try JSON first (for consistency), fall back to single value
          try {
            const parsed = JSON.parse(value)
            if (Array.isArray(parsed)) {
              filterValue.selectValues = parsed
            } else {
              // Not a JSON array, treat as single value
              filterValue.selectValues = [value]
            }
          } catch (e) {
            // Not valid JSON, treat as single value
            filterValue.selectValues = [value]
          }
        } else {
          // STRING field or unknown - treat as string
          filterValue.stringValue = value
        }
      } else {
        // No metadata available - fall back to value-based inference
        // This happens when custom fields haven't loaded yet
        if (value === 'true' || value === 'false') {
          // Boolean value
          filterValue.booleanValue = value === 'true'
        } else if (value.startsWith('[') && value.endsWith(']')) {
          // Try to parse as JSON array (handles values with commas)
          try {
            const parsed = JSON.parse(value)
            if (Array.isArray(parsed)) {
              filterValue.selectValues = parsed
            } else {
              filterValue.stringValue = value
            }
          } catch (e) {
            // Not valid JSON, treat as string
            filterValue.stringValue = value
          }
        } else if (value.includes(',')) {
          // Multi-select values (comma-separated) - backward compatibility
          // Note: This may incorrectly split values containing commas
          // Will be re-parsed correctly when metadata loads
          filterValue.selectValues = value.split(',')
        } else {
          // String value (will be re-parsed when metadata loads)
          filterValue.stringValue = value
        }
      }

      customFieldFilters.push(filterValue)
    }
  })

  if (customFieldFilters.length > 0) {
    filtersCopy.customFields = customFieldFilters
  }

  return filtersCopy
}
